import PropTypes from 'prop-types';
import { compose, withHandlers } from 'recompose';
import { createSelector } from 'reselect';
import { ddp } from '@zedoc/ddp-connector';
import { default as ProjectSelect } from '../../../../common/selectors/Project';
import { default as CSVInputSchemaSelect } from '../../../../common/selectors/CSVInputSchema';
import settings from '../../../../common/settings';
import ProjectMilestones from './ProjectMilestones';
import {
  forceSyncToPatientService,
  apiZedocResyncNotifications,
  apiZedocResyncCompleted,
  apiZedocCountResendCandidates,
  apiZedocResendNotifications,
  apiZedocOneProject,
} from '../../../../common/api/zedoc';
import { callMethod } from '../../../../common/utilsClient/ddp/actions';
import {
  openProjectProfileDialog,
  openEditMilestoneDialog,
  openDownloadResponsesCSVDialog,
} from '../../actions';
import { getResumeToken } from '../../../../common/utilsClient/ddp/selectors';
import { notifyError, notifySuccess } from '../../../../utils/notify';
import CurrentUser from '../../../../models/CurrentUser';
import { apiCurrentUserUserDashboards } from '../../../../common/api/currentUser';
import CurrentUserDashboard from '../../../../models/CurrentUserDashboard';

const { backendUrl } = settings.public;

const getProjectId = (state, { projectId }) => projectId;
const getDashboardId = (state, { dashboardId }) => dashboardId;
const getProject = ProjectSelect.one().whereIdEquals(getProjectId);
const getUserDashboard = CurrentUserDashboard.select
  .one()
  .whereIdEquals(getDashboardId);

const DashboardTimeline = compose(
  ddp({
    subscriptions: (state, { projectId, dashboardId }) => [
      projectId &&
        apiZedocOneProject.withParams({
          projectId,
        }),
      dashboardId &&
        apiCurrentUserUserDashboards.withParams({
          dashboardId,
        }),
    ],
    selectors: () => ({
      project: getProject,
      resumeToken: getResumeToken,
      csvInputSchemas: CSVInputSchemaSelect.all().where({
        projectId: getProjectId,
      }),
      userDashboard: getUserDashboard,
      validate: CurrentUser.select.getCurrentPermissionsValidator({
        relativeTo: createSelector(
          getProject,
          (project) => project && project.getDomains(),
        ),
      }),
    }),
    renderLoader: null,
  }),
  withHandlers({
    onCsvUpload:
      ({ resumeToken }) =>
      (projectId, csvSchemaId, formData) => {
        const url = `${backendUrl}/api/v1/csvUploads/projects/${projectId}/schemas/${csvSchemaId}`;
        // see https://stackoverflow.com/a/35206069/2817257
        return fetch(url, {
          method: 'POST',
          body: formData,
          headers: {
            'X-Auth-Token': resumeToken,
          },
        })
          .then((response) => {
            if (response.ok) {
              return response.blob();
            }
            return response
              .json()
              .then(({ error, reason }) =>
                Promise.reject(new Error(`[${error}] ${reason}`)),
              );
          })
          .then(
            notifySuccess(
              'Successfully uploaded file. Please wait until it is fully processed.',
            ),
          )
          .catch(notifyError());
      },
    onAddPatient:
      ({ dispatch }) =>
      () =>
        dispatch(openProjectProfileDialog({})),
    onAddMilestone:
      ({ dispatch }) =>
      () =>
        dispatch(openEditMilestoneDialog({})),
    onDownloadResponsesCSV:
      ({ dispatch, dashboardId, userDashboard }) =>
      () =>
        console.log({ dashboardId, userDashboard }) ||
        dispatch(
          openDownloadResponsesCSVDialog({
            dashboardId,
            csvResponsesOptions: userDashboard?.csvResponsesOptions || {},
          }),
        ),
    // NOTE: `filters` is the already-cleaned DashboardTimeline filter payload
    //       (cleanFilters(rawFilters, { anyOf: true })) that ProjectMilestones
    //       also passes to apiZedocSearchPatientRecords, so the replay is
    //       scoped to the same filter predicates the operator is looking at.
    // Both resync handlers take the same already-cleaned filter payload as
    // onForceSyncToProms, so all three actions on that menu act on exactly the
    // cohort the operator is looking at.
    onResyncNotifications:
      ({ dispatch, project }) =>
      (filters) =>
        dispatch(
          callMethod(
            apiZedocResyncNotifications,
            {
              projectId: project._id,
              filters,
            },
            {
              noRetry: true,
            },
          ),
        )
          .then(
            // Both numbers, deliberately. They differ whenever a notification
            // never reached the messaging service - cancelled before sending,
            // for instance - and showing only the smaller one reads as a
            // failure when it is the correct answer.
            notifySuccess(({ nRecords, nRequested }) =>
              nRecords > 0
                ? `${nRecords} of ${nRequested} notification statuses are being re-synchronized - please wait`
                : `Nothing to re-synchronize: none of the ${nRequested} matched notifications have been sent yet`,
            ),
          )
          .catch(notifyError()),
    onResyncCompleted:
      ({ dispatch, project }) =>
      (filters) =>
        dispatch(
          callMethod(
            apiZedocResyncCompleted,
            {
              projectId: project._id,
              filters,
            },
            {
              noRetry: true,
            },
          ),
        )
          .then(
            notifySuccess(({ nRecords, nRequested }) =>
              nRecords > 0
                ? `${nRecords} of ${nRequested} responses are being re-synchronized - please wait`
                : `Nothing to re-synchronize: none of the ${nRequested} matched answer sheets have been completed yet`,
            ),
          )
          .catch(notifyError()),
    // Counting is a separate call from sending, and returns the promise so the
    // menu can open its confirmation with the real number. Nothing is sent here.
    onCountResendCandidates:
      ({ dispatch, project }) =>
      (filters) =>
        dispatch(
          callMethod(
            apiZedocCountResendCandidates,
            {
              projectId: project._id,
              filters,
            },
            {
              noRetry: true,
            },
          ),
        ).catch((error) => {
          notifyError()(error);
          // Rethrow so the caller does not open a confirmation dialog showing a
          // count it never received.
          throw error;
        }),
    onResendNotifications:
      ({ dispatch, project }) =>
      (filters, confirmCount) =>
        dispatch(
          callMethod(
            apiZedocResendNotifications,
            {
              projectId: project._id,
              filters,
              confirmCount,
            },
            {
              noRetry: true,
            },
          ),
        )
          .then(
            notifySuccess(
              ({ nActivities }) =>
                `Sending ${nActivities} reminders - this runs in the background and may take a few minutes`,
            ),
          )
          .catch(notifyError()),
    onForceSyncToProms:
      ({ dispatch, project }) =>
      (filters) =>
        dispatch(
          callMethod(
            forceSyncToPatientService,
            {
              projectId: project._id,
              filters,
            },
            {
              noRetry: true,
            },
          ),
        )
          .then(
            notifySuccess(
              ({ nRecords }) =>
                `${nRecords} records are now being synchronized - please wait`,
            ),
          )
          .catch(notifyError()),
  }),
)(ProjectMilestones);

DashboardTimeline.propTypes = {
  projectId: PropTypes.string.isRequired,
  dashboardId: PropTypes.string.isRequired,
};

export default DashboardTimeline;
